import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Edit2, MapPin, Star, Plus, X, Save, LogOut, Camera, Shield, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { browserTz, tzLabel } from '../lib/tz';
import { grossFromNet, feeBreakdown, fmtMoney } from '../lib/fees';
import './Profile.css';

const money2 = (n) => `$${Number(n || 0).toFixed(2)}`;

const PricePreview = ({ net, index }) => {
  if (!(Number(net) > 0)) return null;
  const { gross, platformFee, appStoreFee, stripeFee } = feeBreakdown(net);
  return (
    <p data-testid={`pkg-price-preview-${index}`} style={{ margin: 0, fontSize: 12, color: '#6B7280', lineHeight: 1.4 }}>
      Students pay <strong style={{ color: '#E07A5F' }}>{money2(gross)}</strong> · incl. {money2(platformFee)} platform + {money2(appStoreFee)} app store + {money2(stripeFee)} Stripe
    </p>
  );
};

const Profile = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [myReviews, setMyReviews] = useState({ ratings: [], average_rating: 0, total_ratings: 0 });
  const [tzPickerOpen, setTzPickerOpen] = useState(false);
  const [tzSearch, setTzSearch] = useState('');

  const allTimezones = useMemo(() => {
    try { if (typeof Intl.supportedValuesOf === 'function') return Intl.supportedValuesOf('timeZone'); }
    catch { /* ignore */ }
    return ['UTC','Australia/Sydney','Australia/Melbourne','Australia/Perth','Europe/London','Europe/Paris','Europe/Berlin',
      'America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Toronto',
      'Asia/Tokyo','Asia/Singapore','Asia/Hong_Kong','Asia/Kolkata','Asia/Dubai','Asia/Ho_Chi_Minh','Pacific/Auckland'];
  }, []);
  const filteredTz = useMemo(() => {
    const q = tzSearch.trim().toLowerCase();
    if (!q) return allTimezones;
    return allTimezones.filter(tz => tz.toLowerCase().includes(q) || tz.toLowerCase().replace(/_/g,' ').includes(q));
  }, [tzSearch, allTimezones]);
  const pickTz = (tz) => {
    // Location derives from the IANA city label — one source of truth, no
    // more "moon" slipping through a free-text input.
    setForm(p => ({ ...p, timezone: tz, location: tzLabel(tz) }));
    setTzPickerOpen(false);
    setTzSearch('');
    toast.success('Timezone set', { description: `${tzLabel(tz)} · ${tz}` });
  };

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const d = await api(`/api/creators/${user.id}/ratings?limit=3`);
        setMyReviews({
          ratings: d.ratings || [],
          average_rating: d.average_rating || 0,
          total_ratings: d.total_ratings || 0,
        });
      } catch { /* ignore */ }
    })();
  }, [user?.id]);

  const startEditing = useCallback(() => {
    setForm({
      full_name: user?.full_name || '',
      username: user?.username || '',
      bio: user?.bio || '',
      timezone: user?.timezone || browserTz() || '',
      location: user?.location || '',
      experience: user?.experience || '',
      desired_outcomes: user?.desired_outcomes || '',
      skills: (user?.skills || []).flatMap(s => String(s).split(/[,;\n]+/)).map(s => s.trim()).filter(Boolean),
      social_links: { ...(user?.social_links || {}) },
      booking_packages: [...(user?.booking_packages || [])],
    });
    setEditing(true);
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api('/api/users/me', { method: 'PUT', body: JSON.stringify(form) });
      await refreshUser();
      setEditing(false);
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
    setSaving(false);
  };

  const addSkill = () => {
    const parts = newSkill.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
    if (parts.length) {
      setForm(prev => ({ ...prev, skills: [...prev.skills, ...parts.filter(s => !prev.skills.includes(s))] }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setForm(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addPackage = () => {
    setForm(prev => ({
      ...prev,
      booking_packages: [...prev.booking_packages, { id: `pkg_${Date.now()}`, name: 'New Package', minutes: 30, price: 50 }]
    }));
  };

  const updatePackage = (idx, field, value) => {
    setForm(prev => {
      const pkgs = [...prev.booking_packages];
      pkgs[idx] = { ...pkgs[idx], [field]: field === 'price' || field === 'minutes' ? Number(value) : value };
      return { ...prev, booking_packages: pkgs };
    });
  };

  const removePackage = (idx) => {
    setForm(prev => ({ ...prev, booking_packages: prev.booking_packages.filter((_, i) => i !== idx) }));
  };

  if (!user) return null;

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      await api('/api/users/me/avatar', { method: 'POST', body: formData });
      await refreshUser();
    } catch (err) {
      alert('Avatar upload failed: ' + err.message);
    }
    setAvatarUploading(false);
  };

  return (
    <div className="profile-page" data-testid="profile-page">
      {/* Header */}
      <div className="profile-header-section">
        <div className="profile-header-actions">
          {user.is_admin && (
            <button onClick={() => navigate('/admin')} className="profile-icon-btn" data-testid="admin-btn" title="Admin dashboard">
              <Shield size={20} />
            </button>
          )}
          <button onClick={() => { logout(); navigate('/auth'); }} className="profile-icon-btn" data-testid="logout-btn">
            <LogOut size={20} />
          </button>
          <button onClick={() => navigate('/settings')} className="profile-icon-btn" data-testid="settings-btn">
            <SettingsIcon size={20} />
          </button>
        </div>
        <div className="profile-header-content">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={user.avatar_url || `https://i.pravatar.cc/150?u=${user.id}`} alt={user.full_name} className="profile-main-avatar" style={{ opacity: avatarUploading ? 0.5 : 1 }} />
            <input type="file" ref={avatarInputRef} accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              style={{ position: 'absolute', bottom: 0, right: 0, width: 30, height: 30, borderRadius: '50%', background: '#E07A5F', border: '2px solid #000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              data-testid="avatar-upload-btn"
            >
              <Camera size={14} />
            </button>
          </div>
          <h1 className="profile-display-name">{user.full_name}</h1>
          <p className="profile-username">@{user.username}</p>
          <div className="profile-stats-row">
            <div className="stat"><strong>{user.video_count || 0}</strong><span>Videos</span></div>
            <div className="stat"><strong>{user.followers?.length || 0}</strong><span>Followers</span></div>
            <div className="stat"><strong>{user.following?.length || 0}</strong><span>Following</span></div>
          </div>
          {user.average_rating > 0 && (
            <div className="profile-rating-badge">
              <Star size={16} fill="#D97706" color="#D97706" /> {user.average_rating} ({user.total_ratings} reviews)
            </div>
          )}
        </div>
      </div>

      {/* Edit / View */}
      <div className="profile-body">
        {!editing ? (
          <>
            <button onClick={startEditing} className="edit-profile-btn" data-testid="edit-profile-btn">
              <Edit2 size={16} /> Edit Profile
            </button>

            {user.bio && <div className="profile-section"><h3>About</h3><p>{user.bio}</p></div>}
            {user.location && <div className="profile-section"><h3>Location</h3><p><MapPin size={14} /> {user.location}</p></div>}
            {user.experience && <div className="profile-section"><h3>Experience</h3><p>{user.experience}</p></div>}
            {user.desired_outcomes && <div className="profile-section"><h3>Goals</h3><p>{user.desired_outcomes}</p></div>}

            {user.skills?.length > 0 && (
              <div className="profile-section">
                <h3>Skills</h3>
                <div className="skills-list">{user.skills.map((s, i) => <span key={i} className="skill-chip">{s}</span>)}</div>
              </div>
            )}

            {user.booking_packages?.length > 0 && (
              <div className="profile-section">
                <h3>Booking Packages</h3>
                <div className="packages-list">
                  {user.booking_packages.map((pkg, i) => (
                    <div key={i} className="package-card">
                      <strong>{pkg.name}</strong>
                      <span data-testid={`pkg-summary-${i}`}>{pkg.minutes} min — You get {fmtMoney(pkg.price)} · Students pay {fmtMoney(grossFromNet(pkg.price))}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {myReviews.total_ratings > 0 && (
              <div className="profile-section" data-testid="profile-reviews">
                <h3>Ratings &amp; Reviews</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#fff' }}>
                  <Star size={16} fill="#D97706" color="#D97706" />
                  <strong style={{ fontSize: 18 }}>{myReviews.average_rating.toFixed(1)}</strong>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>· {myReviews.total_ratings} review{myReviews.total_ratings === 1 ? '' : 's'}</span>
                </div>
                <div>
                  {myReviews.ratings.slice(0, 3).map((r) => (
                    <div key={r.id} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} size={12} fill={i <= Math.round(r.overall_rating) ? '#D97706' : 'transparent'} color={i <= Math.round(r.overall_rating) ? '#D97706' : '#4B5563'} />
                        ))}
                        <span style={{ color: '#fff', fontSize: 13, marginLeft: 4 }}>{r.overall_rating.toFixed(1)}</span>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginLeft: 8 }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : ''}
                        </span>
                      </div>
                      {r.review_text && <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, margin: '6px 0 0', lineHeight: 1.4 }}>{r.review_text}</p>}
                    </div>
                  ))}
                </div>
                {myReviews.total_ratings > 3 && (
                  <button
                    onClick={() => navigate(`/discovery/${user.id}`)}
                    style={{ background: 'none', border: 'none', color: '#E07A5F', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 14 }}
                    data-testid="see-all-reviews-btn"
                  >
                    See all reviews →
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="edit-form" data-testid="profile-edit-form">
            <div className="form-group">
              <label>Full Name</label>
              <input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} data-testid="edit-name" />
            </div>
            <div className="form-group">
              <label>Username</label>
              <input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} data-testid="edit-username" />
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={3} data-testid="edit-bio" />
            </div>
            <div className="form-group">
              <label>Location &amp; Timezone</label>
              <button
                type="button"
                onClick={() => setTzPickerOpen(true)}
                data-testid="edit-tz-row"
                style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}
              >
                <Globe size={18} style={{ color: '#2C4E46', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#111' }}>
                    {form.timezone ? tzLabel(form.timezone) : 'Choose your timezone'}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>
                    {form.timezone || 'Tap to pick — determines your booking clock'}
                  </p>
                </div>
                <span style={{ padding: '4px 10px', borderRadius: 999, background: '#F3F4F6', color: '#374151', fontSize: 11, fontWeight: 700 }}>CHANGE</span>
              </button>
            </div>
            <div className="form-group">
              <label>Experience</label>
              <textarea value={form.experience} onChange={e => setForm(p => ({ ...p, experience: e.target.value }))} rows={3} />
            </div>
            <div className="form-group">
              <label>Goals / Desired Outcomes</label>
              <textarea value={form.desired_outcomes} onChange={e => setForm(p => ({ ...p, desired_outcomes: e.target.value }))} rows={3} />
            </div>
            <div className="form-group">
              <label>Skills</label>
              <div className="skills-list">{form.skills.map((s, i) => (
                <span key={i} className="skill-chip">{s} <button onClick={() => removeSkill(s)}><X size={12} /></button></span>
              ))}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input value={newSkill} onChange={e => setNewSkill(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSkill()} placeholder="Add a skill..." style={{ flex: 1 }} data-testid="skill-input" />
                <button onClick={addSkill} className="add-btn" data-testid="skill-add-btn"><Plus size={16} /></button>
              </div>
            </div>
            <div className="form-group">
              <label>Booking Packages</label>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: '#6B7280', lineHeight: 1.4 }}>
                Set the amount you want to <strong>take home</strong> per session. We'll add our platform fee, the app store fee and Stripe's processing fee on top so the student sees the full price when they book.
              </p>
              {form.booking_packages.length > 0 && (
                <div className="package-edit-row package-edit-head" aria-hidden="true" style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, textTransform: 'uppercase', textAlign: 'center' }}>Topic</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, textTransform: 'uppercase', textAlign: 'center' }}>Mins</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, textTransform: 'uppercase', textAlign: 'center' }}>Your take ($)</span>
                  <span />
                </div>
              )}
              {form.booking_packages.map((pkg, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div className="package-edit-row" style={{ marginBottom: 4 }}>
                    <input value={pkg.name} onChange={e => updatePackage(i, 'name', e.target.value)} placeholder="e.g. Resume review" data-testid={`pkg-name-input-${i}`} />
                    <input type="number" value={pkg.minutes} onChange={e => updatePackage(i, 'minutes', e.target.value)} placeholder="30" data-testid={`pkg-minutes-input-${i}`} />
                    <input type="number" value={pkg.price} onChange={e => updatePackage(i, 'price', e.target.value)} placeholder="50" data-testid={`pkg-price-input-${i}`} />
                    <button onClick={() => removePackage(i)} className="remove-btn" data-testid={`pkg-remove-${i}`}><X size={14} /></button>
                  </div>
                  <PricePreview net={pkg.price} index={i} />
                </div>
              ))}
              <button onClick={addPackage} className="add-btn" style={{ marginTop: 8 }} data-testid="pkg-add-btn"><Plus size={14} /> Add Package</button>
            </div>
            <div className="form-group">
              <label>Social Links</label>
              {['instagram', 'twitter', 'linkedin', 'youtube'].map(platform => (
                <input key={platform} value={form.social_links[platform] || ''} onChange={e => setForm(p => ({ ...p, social_links: { ...p.social_links, [platform]: e.target.value } }))} placeholder={platform.charAt(0).toUpperCase() + platform.slice(1) + ' URL'} style={{ marginBottom: 8 }} />
              ))}
            </div>
            <div className="edit-actions">
              <button onClick={() => setEditing(false)} className="cancel-btn">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="save-btn" data-testid="save-profile-btn">
                <Save size={16} /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Timezone picker modal */}
      {tzPickerOpen && (
        <div
          data-testid="edit-tz-picker"
          onClick={() => { setTzPickerOpen(false); setTzSearch(''); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 20 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700 }}>Choose your timezone</h3>
            <input
              autoFocus
              type="search"
              value={tzSearch}
              onChange={e => setTzSearch(e.target.value)}
              placeholder="Search city or region"
              data-testid="edit-tz-search"
              style={{ padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 15, marginBottom: 12 }}
            />
            <div style={{ overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
              {filteredTz.length === 0 ? (
                <p style={{ padding: 20, textAlign: 'center', color: '#6B7280' }}>No zones match "{tzSearch}"</p>
              ) : (
                filteredTz.slice(0, 200).map(tz => {
                  const isCurrent = tz === form.timezone;
                  return (
                    <button
                      key={tz}
                      onClick={() => pickTz(tz)}
                      disabled={isCurrent}
                      data-testid={`edit-tz-opt-${tz.replace(/\//g,'-')}`}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', background: isCurrent ? '#F0FDF4' : '#fff', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: isCurrent ? 'default' : 'pointer' }}
                    >
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111' }}>
                        {tzLabel(tz)}{isCurrent && <span style={{ marginLeft: 8, fontSize: 11, color: '#166534', fontWeight: 700 }}>· CURRENT</span>}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>{tz}</p>
                    </button>
                  );
                })
              )}
            </div>
            {filteredTz.length > 200 && (
              <p style={{ marginTop: 8, fontSize: 12, color: '#6B7280', textAlign: 'center' }}>Refine your search for more.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
